import { WORKSPACE_HEADER, getWorkspace } from "./workspace";

export const API_ROOT = "/api/v1";
export const AUTH_ROOT = "/api/auth";
export const CLIENT_HEADER = "X-Mk-Client";

export interface ApiErrorBody {
    snapshot?: unknown;
    code: string;
    message: string;
    details?: { field: string | null; message: string }[];
}

export class ApiError extends Error {
    readonly status: number;
    readonly code: string;
    readonly details: ApiErrorBody["details"];
    readonly snapshot?: unknown;

    constructor(status: number, body: ApiErrorBody) {
        super(body.message);
        this.name = "ApiError";
        this.status = status;
        this.code = body.code;
        this.details = body.details;
        this.snapshot = body.snapshot;
    }
}

/** The most rows the server sends in one page (`MAX_PER_PAGE` in promaster_core). */
export const LIST_PAGE_MAX = 200;
/** How many pages `listAll` reads at once. */
const LIST_PARALLEL = 4;

export interface Page<T> {
    data: T[];
    meta: { page: number; per_page: number; total: number; pages: number };
}

/** A language this deployment can answer in, as the server names it. */
export interface ServerLanguage {
    code: string;
    ui: string;
    name: string;
    direction: "ltr" | "rtl";
}

const SAFE = new Set(["GET", "HEAD"]);

let language = "en";

export function setApiLanguage(code: string) {
    language = code;
}

async function unwrap(response: Response): Promise<unknown> {
    if (response.status === 204) {
        return null;
    }
    let payload: unknown = null;
    try {
        payload = await response.json();
    } catch {
        payload = null;
    }
    const body = payload as { error?: ApiErrorBody; login_url?: string } | null;
    if (!response.ok || body?.error) {
        const failure = new ApiError(response.status, body?.error ?? {
            code: String(response.status),
            message: response.statusText || "Request failed",
        });
        if (body?.login_url) {
            (failure as ApiError & { loginUrl?: string }).loginUrl = body.login_url;
        }
        throw failure;
    }
    return payload;
}

async function send(root: string, path: string, method: string, body?: unknown,
                    signal?: AbortSignal): Promise<unknown> {
    const headers: Record<string, string> = {
        Accept: "application/json",
        "Accept-Language": language,
        [WORKSPACE_HEADER]: getWorkspace(),
    };
    if (body !== undefined) {
        headers["Content-Type"] = "application/json";
    }
    if (!SAFE.has(method)) {
        headers[CLIENT_HEADER] = "promaster-react";
    }
    if (!SAFE.has(method) && !navigator.onLine) {
        throw new ApiError(0, { code: "offline", message: language.startsWith("ar")
            ? "الاتصال بالإنترنت مطلوب لحفظ التغييرات. حاول مجددًا بعد عودة الاتصال."
            : "An internet connection is required to save changes. Try again when connected." });
    }
    const response = await fetch(`${root}${path}`, {
        method,
        credentials: "same-origin",
        cache: "no-store",
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal,
    });
    return unwrap(response);
}

function query(params?: Record<string, string | number | boolean | undefined>): string {
    if (!params) {
        return "";
    }
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== "") {
            search.set(key, String(value));
        }
    }
    const text = search.toString();
    return text ? `?${text}` : "";
}

/** What a workflow step wants said to whoever took it. */
export interface Notice {
    title?: string;
    message: string;
    type?: "success" | "info" | "warning" | "danger";
}

export const api = {
    raw<T>(path: string, signal?: AbortSignal): Promise<{ data: T }> {
        return send(API_ROOT, path, "GET", undefined, signal) as Promise<{ data: T }>;
    },
    post<T>(path: string, values?: unknown): Promise<{ data: T }> {
        return send(API_ROOT, path, "POST", values ?? {}) as Promise<{ data: T }>;
    },
    put<T>(path: string, values?: unknown): Promise<{ data: T }> {
        return send(API_ROOT, path, "PUT", values ?? {}) as Promise<{ data: T }>;
    },
    patch<T>(path: string, values?: unknown): Promise<{ data: T }> {
        return send(API_ROOT, path, "PATCH", values ?? {}) as Promise<{ data: T }>;
    },
    list<T>(resource: string, params?: Record<string, string | number | boolean | undefined>,
            signal?: AbortSignal): Promise<Page<T>> {
        return send(API_ROOT, `/${resource}${query(params)}`, "GET", undefined, signal) as Promise<Page<T>>;
    },
    /** Every row of a list. The server answers at most LIST_PAGE_MAX rows at a
     *  time, so the pages after the first are read a few at once and joined in order. */
    async listAll<T>(resource: string, params?: Record<string, string | number | boolean | undefined>,
                     signal?: AbortSignal): Promise<Page<T>> {
        const first = await api.list<T>(resource, { ...params, page: 1, per_page: LIST_PAGE_MAX }, signal);
        const pages = Math.max(Number(first.meta?.pages ?? 1), 1);
        const rest: T[][] = [];
        const waiting = Array.from({ length: pages - 1 }, (_, index) => index + 2);
        async function worker() {
            for (let page = waiting.shift(); page !== undefined; page = waiting.shift()) {
                const answer = await api.list<T>(resource, { ...params, page, per_page: LIST_PAGE_MAX }, signal);
                rest[page] = answer.data ?? [];
            }
        }
        await Promise.all(Array.from({ length: Math.min(LIST_PARALLEL, waiting.length) }, worker));
        const data = [...(first.data ?? []), ...rest.filter(Boolean).flat()];
        return { data, meta: { page: 1, per_page: data.length, total: data.length, pages: 1 } };
    },
    get<T>(resource: string, id: number, signal?: AbortSignal): Promise<{ data: T }> {
        return send(API_ROOT, `/${resource}/${id}`, "GET", undefined, signal) as Promise<{ data: T }>;
    },
    create<T>(resource: string, values: unknown): Promise<{ data: T }> {
        return send(API_ROOT, `/${resource}`, "POST", values) as Promise<{ data: T }>;
    },
    update<T>(resource: string, id: number, values: unknown): Promise<{ data: T }> {
        return send(API_ROOT, `/${resource}/${id}`, "PATCH", values) as Promise<{ data: T }>;
    },
    remove(resource: string, id: number): Promise<null> {
        return send(API_ROOT, `/${resource}/${id}`, "DELETE") as Promise<null>;
    },
    act<T>(resource: string, id: number, action: string,
           values?: unknown): Promise<{ data: T; meta?: { notice?: Notice } }> {
        return send(API_ROOT, `/${resource}/${id}/${action}`, "POST",
                    values ?? {}) as Promise<{ data: T; meta?: { notice?: Notice } }>;
    },
    nested<T>(resource: string, id: number, name: string,
              params?: Record<string, string | number | boolean | undefined>,
              signal?: AbortSignal): Promise<{ data: T[] }> {
        return send(API_ROOT, `/${resource}/${id}/${name}${query(params)}`, "GET",
                    undefined, signal) as Promise<{ data: T[] }>;
    },
};

export const auth = {
    /** Signing out ends the Keycloak session too, so the next visit is asked again. */
    logoutUrl(): string {
        const back = `${window.location.origin}/`;
        return `${AUTH_ROOT}/sso/logout?redirect=${encodeURIComponent(back)}`;
    },
    chooseCompany(companyId: number) {
        return send(AUTH_ROOT, "/me/company", "PUT", { company_id: companyId }) as Promise<{
            data: { company: { id: number; name: string } | null };
        }>;
    },
    me(signal?: AbortSignal) {
        return send(AUTH_ROOT, "/me", "GET", undefined, signal) as Promise<{
            data: { id: number; name: string; login: string; is_admin: boolean;
                    lang: string; ui_lang: string | null; direction: "ltr" | "rtl";
                    date_format?: string; time_format?: string;
                    company: { id: number; name: string } | null;
                    companies: { id: number; name: string }[] };
        }>;
    },
    languages(signal?: AbortSignal) {
        return send(AUTH_ROOT, "/languages", "GET", undefined, signal) as Promise<{
            data: ServerLanguage[];
        }>;
    },
    async chooseLanguage(code: string) {
        const payload = await send(API_ROOT, "/me/profile", "PATCH", { lang: code }) as {
            data: { lang: string; date_format?: string; time_format?: string };
        };
        const { lang, date_format, time_format } = payload.data;
        const rtl = /^(ar|he|fa|ur)(_|$)/.test(lang);
        return { data: { lang, ui_lang: lang.split("_")[0], direction: rtl ? "rtl" : "ltr", date_format,
                         time_format } } as {
            data: { lang: string; ui_lang: string | null; direction: "ltr" | "rtl"; date_format?: string;
                    time_format?: string };
        };
    },
};
